import React, { useState, useEffect, useCallback } from 'react';
import { FaChevronDown, FaChevronRight, FaFlask, FaCalendarAlt, FaTrash, FaEnvelope } from 'react-icons/fa';
import './css_pages/ViewPrepOrders.css';
import { useStore } from '../contexts/StoreContext';
import {
  getPrepOrderDates,
  getPrepOrdersForView,
  deletePrepOrder
} from '../services/prepOrderingViewService';

const toDateString = (val) => {
  if (!val) return null;
  if (val instanceof Date) return val.toISOString().split('T')[0];
  return String(val).split('T')[0];
};

function ViewPrepOrders() {
  const { selectedStore } = useStore();

  const [filterDate, setFilterDate]         = useState('');
  const [availableDates, setAvailableDates] = useState([]);
  const [orders, setOrders]                 = useState([]);
  const [loading, setLoading]               = useState(false);
  const [error, setError]                   = useState(null);
  const [expandedOrders, setExpandedOrders] = useState({});

  // ── Fechas ──
  const fetchDates = useCallback(async () => {
    if (!selectedStore) return;
    try {
      const response = await getPrepOrderDates(selectedStore.id);
      if (response.success) {
        setAvailableDates(response.data);
        setFilterDate('');
        setOrders([]);
      }
    } catch {
      setAvailableDates([]);
    }
  }, [selectedStore]);

  useEffect(() => { fetchDates(); }, [fetchDates]);

  // ── Órdenes ──
  const fetchOrders = useCallback(async () => {
    if (!selectedStore || !filterDate) return;
    try {
      setLoading(true); setError(null);
      const cleanDate = toDateString(filterDate);
      const response = await getPrepOrdersForView(selectedStore.id, cleanDate);
      if (response.success) {
        setOrders(response.orders);
        setExpandedOrders({});
      }
    } catch {
      setError('Error loading prep orders');
    } finally {
      setLoading(false);
    }
  }, [selectedStore, filterDate]);

  useEffect(() => { fetchOrders(); }, [fetchOrders]);

  // ── Expand / collapse ──
  const toggleExpand = (id) => setExpandedOrders(prev => ({ ...prev, [id]: !prev[id] }));
  const expandAll = () => {
    const all = {};
    orders.forEach(o => { all[o.id_prep_order] = true; });
    setExpandedOrders(all);
  };
  const collapseAll = () => setExpandedOrders({});

  const handleDeleteOrder = async (id) => {
    if (!window.confirm('Are you sure you want to delete this prep order? This action cannot be undone.')) return;
    try {
      await deletePrepOrder(id);
      setOrders(prev => prev.filter(o => o.id_prep_order !== id));
    } catch {
      alert('Error deleting prep order');
    }
  };

  // ── Formato ──
  const formatDateTime = (dateStr) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleString('es-MX', {
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', hour12: true
    });
  };
  const formatDateLong = (dateStr) => {
    if (!dateStr) return '—';
    const d = new Date(dateStr.includes('T') ? dateStr : dateStr + 'T00:00:00');
    return d.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
  };

  const totalItems = orders.reduce((sum, o) => sum + (o.items?.length || 0), 0);

  if (!selectedStore) {
    return (
      <div className="vpo-page">
        <div className="page-header"><h1>View Prep Orders</h1></div>
        <div className="no-store-message">
          <p>Please select a store from the sidebar to view prep orders</p>
        </div>
      </div>
    );
  }

  return (
    <div className="vpo-page">

      {/* HEADER */}
      <div className="page-header sticky-header">
        <div className="header-left">
          <h1>View Prep Orders</h1>
          <span className="store-badge">{selectedStore.name}</span>
        </div>
      </div>

      {/* FILTRO (solo por fecha de envío) */}
      <div className="vpo-filters">
        <div className="vpo-date-selector">
          <label>Select sent date:</label>
          <select
            value={filterDate}
            onChange={(e) => setFilterDate(e.target.value)}
            className="vpo-select"
            disabled={availableDates.length === 0}
          >
            <option value="">-- Select a date --</option>
            {availableDates.map(d => (
              <option key={d.date} value={d.date}>
                {formatDateLong(d.date)} ({d.order_count} order{d.order_count !== 1 ? 's' : ''})
              </option>
            ))}
          </select>
        </div>
      </div>

      {error && (
        <div className="error-message">
          {error}
          <button onClick={fetchOrders}>Retry</button>
        </div>
      )}

      {loading && <div className="loading">Loading prep orders...</div>}

      {!loading && filterDate && orders.length === 0 && (
        <div className="vpo-empty">
          <FaFlask style={{ fontSize: 32, color: '#cbd5e1', marginBottom: 12 }} />
          <p>No prep orders found for this date</p>
        </div>
      )}

      {!loading && orders.length > 0 && (
        <>
          {/* SUMMARY */}
          <div className="vpo-summary-bar">
            <div className="vpo-summary-stats">
              <span className="vpo-stat"><strong>{orders.length}</strong> order{orders.length !== 1 ? 's' : ''}</span>
              <span className="vpo-stat-divider">·</span>
              <span className="vpo-stat"><strong>{totalItems}</strong> prep{totalItems !== 1 ? 's' : ''}</span>
            </div>
            <div className="vpo-expand-actions">
              <button className="vpo-btn-text" onClick={expandAll}>Expand all</button>
              <span>·</span>
              <button className="vpo-btn-text" onClick={collapseAll}>Collapse all</button>
            </div>
          </div>

          {/* LISTA */}
          <div className="vpo-orders-list">
            {orders.map((order) => {
              const isExpanded = !!expandedOrders[order.id_prep_order];
              return (
                <div key={order.id_prep_order} className={`vpo-order-card ${isExpanded ? 'expanded' : ''}`}>

                  <div className="vpo-order-header" onClick={() => toggleExpand(order.id_prep_order)}>
                    <div className="vpo-order-chevron">
                      {isExpanded ? <FaChevronDown /> : <FaChevronRight />}
                    </div>

                    <div className="vpo-order-meta">
                      <span className="vpo-order-number">{order.order_number}</span>
                      {order.sent_to && (
                        <span className="vpo-order-sentto"><FaEnvelope className="vpo-sentto-icon" /> {order.sent_to}</span>
                      )}
                    </div>

                    <div className="vpo-order-dates">
                      <span className="vpo-order-date-label">Sent:</span>
                      <span className="vpo-order-date-value">{formatDateTime(order.sent_at)}</span>
                    </div>

                    <div className="vpo-order-badge">
                      <span className="vpo-items-count">
                        {order.items?.length || 0} prep{(order.items?.length || 0) !== 1 ? 's' : ''}
                      </span>
                    </div>

                    <button
                      className="vpo-btn-delete"
                      onClick={(e) => { e.stopPropagation(); handleDeleteOrder(order.id_prep_order); }}
                      title="Delete prep order"
                    >
                      <FaTrash />
                    </button>
                  </div>

                  {isExpanded && (
                    <div className="vpo-order-body">
                      <table className="vpo-products-table">
                        <thead>
                          <tr>
                            <th>Preparation</th>
                            <th>Quantity</th>
                            <th>Unit</th>
                          </tr>
                        </thead>
                        <tbody>
                          {order.items && order.items.length > 0 ? (
                            order.items.map((item, idx) => (
                              <tr key={`${order.id_prep_order}-${idx}`}>
                                <td className="vpo-cell-name">{item.prep_name}</td>
                                <td className="vpo-cell-qty">{item.quantity}</td>
                                <td className="vpo-cell-unit">{item.unit || '—'}</td>
                              </tr>
                            ))
                          ) : (
                            <tr>
                              <td colSpan="3" style={{ textAlign: 'center', color: '#94a3b8', padding: '20px' }}>
                                No preps in this order
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </>
      )}

      {!loading && !filterDate && (
        <div className="vpo-prompt">
          <FaCalendarAlt style={{ fontSize: 40, color: '#cbd5e1', marginBottom: 16 }} />
          <p>Select a date above to view prep orders</p>
        </div>
      )}

    </div>
  );
}

export default ViewPrepOrders;